/** Pure metric formatters for the state cards — vendored from scio3 `mdm-devices/utils/device-metrics.ts`. */

export function formatBytes(bytes: number | null | undefined): string | null {
  if (!bytes || !Number.isFinite(bytes) || bytes <= 0) return null;
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = bytes;
  let i = 0;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i += 1;
  }
  return `${size.toFixed(size >= 10 ? 0 : 1)} ${units[i]}`;
}

const COMMON_RATIOS: ReadonlyArray<readonly [number, number]> = [
  [16, 9],
  [16, 10],
  [4, 3],
  [3, 2],
  [5, 4],
  [21, 9],
  [32, 9],
  [9, 16],
  [10, 16],
  [3, 4],
  [1, 1],
];

/** "16:9" / "9:16" — snaps to a common ratio within 2%, else reduces by GCD. Null on bad input. */
export function simplifyRatio(width: number, height: number): string | null {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return null;
  const target = width / height;
  for (const [rw, rh] of COMMON_RATIOS) {
    if (Math.abs(rw / rh - target) / target <= 0.02) return `${rw}:${rh}`;
  }
  const w = Math.round(width);
  const h = Math.round(height);
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const g = gcd(w, h) || 1;
  if (w / g <= 50 && h / g <= 50) return `${w / g}:${h / g}`;
  return `${target.toFixed(2)}:1`;
}

/** Seconds since boot → "5d 12h" / "3h 24m" / "12m" / "45s". Null for missing input. */
export function formatUptimeDuration(seconds: number | undefined): string | null {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return null;
  const s = Math.floor(seconds);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${s}s`;
}
